import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Button, Divider, Grid2 as Grid, TextField, Typography } from '@mui/material';
import PlanFormLayout from '../../plans/components/PlanFormLayout';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPlanById, fetchPlansRequest } from '../../api/plans/planSlice';
import { fetchOrganizationById, fetchOrganizationsRequest } from '../../api/organizations/organizationSlice';
import { selectUser } from '../../api/auth/authSlice';

const SubscriptionForm = ({ initialData, onSubmit, onCancel, buttonText = 'Submit', isEdit = false }) => {
  const [formData, setFormData] = useState(initialData);
  const user = useSelector(selectUser);
  const { organizations, selectedOrganization } = useSelector((state) => state.organizations);
  const { plans, selectedPlan, loading: planLoading } = useSelector((state) => state.plans);
  const dispatch = useDispatch();
  const [organizationSearchKey, setOrganizationSearchKey] = useState('');
  const [planSearchKey, setPlanSearchKey] = useState('');
  const lastProcessedPlanIdRef = useRef(null);

  const updateFormData = useCallback((newData) => {
    setFormData((prev) => ({
      ...prev,
      documents_limit: newData.documents_limit,
    }));
  }, []);

  useEffect(() => {
    dispatch(
      fetchOrganizationsRequest({
        search_by: 'name',
        search_key: organizationSearchKey,
        page: 1,
        limit: 20,
        only_individuals: user.role === 'partner',
      }),
    );
  }, [dispatch, organizationSearchKey]);

  useEffect(() => {
    dispatch(
      fetchPlansRequest({
        search_by: 'name',
        search_key: planSearchKey,
        page: 1,
        limit: 20,
      }),
    );
  }, [dispatch, planSearchKey]);

  useEffect(() => {
    if (
      !isEdit &&
      !planLoading &&
      selectedPlan?._id === formData.plan_id &&
      Object.keys(selectedPlan || {}).length > 0 &&
      lastProcessedPlanIdRef.current !== selectedPlan._id
    ) {
      lastProcessedPlanIdRef.current = selectedPlan._id;
      updateFormData(selectedPlan);
    }
  }, [selectedPlan?._id, isEdit, formData.plan_id, updateFormData, planLoading]);

  useEffect(() => {
    if (!formData.organization_id) return;
    dispatch(fetchOrganizationById(formData.organization_id));
  }, [formData.organization_id, dispatch]);

  useEffect(() => {
    if (!formData.plan_id) return;
    dispatch(fetchPlanById(formData.plan_id));
  }, [formData.plan_id, dispatch]);

  const planFormData = useMemo(
    () =>
      formData.plan_id && selectedPlan && !planLoading && Object.keys(selectedPlan).length > 0 // Wait until plan is loaded
        ? {
            ...selectedPlan,
            documents_limit: formData.documents_limit,
          }
        : null,
    [selectedPlan, formData.plan_id, formData.documents_limit, planLoading],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
    };
    onSubmit(payload);
  };

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={6}>
            {isEdit ? (
              <Autocomplete
                disabled={isEdit}
                options={organizations}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                getOptionKey={(option) => option._id}
                value={isEdit ? selectedOrganization : null}
                onInputChange={(_, newInputValue) => {
                  setOrganizationSearchKey(newInputValue);
                }}
                onChange={(_, newValue) => {
                  setFormData((prev) => ({ ...prev, organization_id: newValue ? newValue._id : null }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Organization" placeholder="Search by Name" size="small" />
                )}
              />
            ) : (
              <Autocomplete
                disabled={isEdit}
                options={organizations}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                getOptionKey={(option) => option._id}
                onInputChange={(_, newInputValue) => {
                  setOrganizationSearchKey(newInputValue);
                }}
                onChange={(_, newValue) => {
                  setFormData((prev) => ({ ...prev, organization_id: newValue ? newValue._id : null }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Organization" placeholder="Search by Name" size="small" />
                )}
              />
            )}
          </Grid>

          <Grid size={6}>
            {isEdit ? (
              <Autocomplete
                disabled={isEdit}
                options={plans}
                getOptionLabel={(option) => option.name}
                getOptionKey={(option) => option._id}
                onInputChange={(_, newInputValue) => setPlanSearchKey(newInputValue)}
                value={isEdit ? selectedPlan : null}
                onChange={(_, newValue) => {
                  if (newValue) {
                    setFormData((prev) => ({ ...prev, plan_id: newValue?._id }));
                  } else {
                    setFormData((prev) => ({
                      ...initialData,
                      organization_id: prev.organization_id,
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Plan" placeholder="Search by Plan Name" size="small" fullWidth />
                )}
              />
            ) : (
              <Autocomplete
                disabled={isEdit}
                options={plans}
                getOptionLabel={(option) => option.name}
                getOptionKey={(option) => option._id}
                onInputChange={(_, newInputValue) => setPlanSearchKey(newInputValue)}
                onChange={(_, newValue) => {
                  if (newValue) {
                    setFormData((prev) => ({ ...prev, plan_id: newValue?._id }));
                  } else {
                    setFormData((prev) => ({
                      ...initialData,
                      organization_id: prev.organization_id,
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Plan" placeholder="Search by Plan Name" size="small" fullWidth />
                )}
              />
            )}
          </Grid>
        </Grid>

        {planFormData ? (
          <>
            <Divider textAlign="center" sx={{ mt: '2rem', mb: '2rem' }}>
              <Typography variant="h6" color="text.secondary">
                Plan Details
              </Typography>
            </Divider>
            <PlanFormLayout
              formData={planFormData}
              updateFormData={updateFormData}
              disableFields={true}
              isEdit={isEdit}
            />
          </>
        ) : null}

        <Box
          sx={{
            float: 'right',
            mt: 2,
            right: 0,
          }}
        >
          <Button variant="outlined" color="info" sx={{ mr: 2 }} onClick={onCancel}>
            Cancel
          </Button>

          <Button type="submit" variant="contained" color="primary">
            {buttonText}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default SubscriptionForm;
